import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, handleApiError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { KeyIcon, ShieldCheckIcon, UserIcon } from '@heroicons/react/24/outline';
import type { ApiKey } from '../lib/types';

const profileSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
});

type ProfileForm = z.infer<typeof profileSchema>;

export default function Settings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showApiKey, setShowApiKey] = useState<string | null>(null);

  const { data: apiKeys } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ apiKeys: ApiKey[] }>('/users/api-keys'),
  });

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: ProfileForm) => api.patch('/users/profile', data),
    onSuccess: () => {
      toast.success('Profile updated');
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: handleApiError,
  });

  const createApiKeyMutation = useMutation({
    mutationFn: (name: string) => api.post<{ apiKey: ApiKey }>('/users/api-keys', { name }),
    onSuccess: (data) => {
      toast.success('API key created');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setShowApiKey(data.apiKey.key || null);
    },
    onError: handleApiError,
  });

  const deleteApiKeyMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/users/api-keys/${id}`),
    onSuccess: () => {
      toast.success('API key deleted');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: handleApiError,
  });

  const handleCreateApiKey = () => {
    const name = prompt('Enter a name for this API key:');
    if (name) {
      createApiKeyMutation.mutate(name);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl">
      <h1 className="text-2xl font-bold text-white">Settings</h1>

      {/* Profile Section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <UserIcon className="w-6 h-6 text-primary-400" />
          <h2 className="text-lg font-semibold text-white">Profile</h2>
        </div>

        <form onSubmit={profileForm.handleSubmit((d) => updateProfileMutation.mutate(d))} className="space-y-4">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input bg-dark-900" value={user?.email} disabled />
          </div>
          <div>
            <label className="label">Name</label>
            <input {...profileForm.register('name')} type="text" className="input" />
            {profileForm.formState.errors.name && (
              <p className="mt-1 text-sm text-red-400">{profileForm.formState.errors.name.message}</p>
            )}
          </div>
          <button type="submit" className="btn-primary" disabled={updateProfileMutation.isPending}>
            {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* API Keys Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <KeyIcon className="w-6 h-6 text-primary-400" />
            <h2 className="text-lg font-semibold text-white">API Keys</h2>
          </div>
          <button onClick={handleCreateApiKey} className="btn-primary" disabled={createApiKeyMutation.isPending}>
            Create Key
          </button>
        </div>

        {showApiKey && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-sm text-green-400 mb-2">Your new API key:</p>
            <code className="block bg-dark-900 p-2 rounded text-white font-mono text-sm break-all">
              {showApiKey}
            </code>
            <p className="text-xs text-gray-400 mt-2">
              Make sure to copy it now. You won't be able to see it again.
            </p>
            <button onClick={() => setShowApiKey(null)} className="btn-ghost mt-2 text-sm">
              Got it
            </button>
          </div>
        )}

        {apiKeys?.apiKeys && apiKeys.apiKeys.length > 0 ? (
          <div className="space-y-3">
            {apiKeys.apiKeys.map((key) => (
              <div key={key.id} className="flex items-center justify-between p-3 bg-dark-900 rounded-lg">
                <div>
                  <p className="font-medium text-white">{key.name}</p>
                  <p className="text-xs text-gray-400">
                    Created {new Date(key.created_at).toLocaleDateString()}
                    {key.last_used_at && ` • Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (confirm('Delete this API key?')) {
                      deleteApiKeyMutation.mutate(key.id);
                    }
                  }}
                  className="btn-ghost text-red-400 hover:text-red-300 text-sm"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">No API keys yet. Create one to integrate with external services.</p>
        )}
      </div>

      {/* Security Section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheckIcon className="w-6 h-6 text-primary-400" />
          <h2 className="text-lg font-semibold text-white">Security</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Password</p>
              <p className="text-sm text-gray-400">Last changed: Never</p>
            </div>
            <button className="btn-outline text-sm">Change Password</button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Two-Factor Authentication</p>
              <p className="text-sm text-gray-400">Add an extra layer of security</p>
            </div>
            <button className="btn-outline text-sm">Enable</button>
          </div>
        </div>
      </div>

      {/* Free Plan Section */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Your Plan</h2>
        <div className="p-4 bg-dark-900 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary-400/20 rounded-lg flex items-center justify-center">
              <span className="text-xl">🎉</span>
            </div>
            <div>
              <p className="text-xl font-semibold text-primary-400">Free Plan</p>
              <p className="text-sm text-green-400">Unlimited access - No payment required</p>
            </div>
          </div>
          <p className="text-sm text-gray-400">
            You have full access to all features. Enjoy unlimited campaigns, templates, and recipients!
          </p>
        </div>
      </div>
    </div>
  );
}
